/**
 * Centralized form validation (Swift-style validation class).
 * All form rules live here: Contact form and Schedule Visit form.
 * Handles typing, paste, and autofill — validation runs on current field values.
 */

import { toDateString } from '@constants/semester';
import { isFullName, isValidEmail, isValidPhone } from './validationUtils';

/** Result of validating a form: valid flag and per-field error messages. */
export interface FormValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

/** Contact form field values. */
export interface ContactFormFields {
  name: string;
  email: string;
  phone: string;
  message: string;
}

/** Schedule Visit form field values. */
export interface ScheduleVisitFormFields {
  name: string;
  email: string;
  phone: string;
  visitDate: string;
  /**
   * When false (no active semester / no future Thursdays), visitDate is optional.
   * Defaults to true for backward compatibility.
   */
  requireVisitDate?: boolean;
}

const ERRORS = {
  fullName: 'Please enter your full name (first and last)',
  email: 'Please enter a valid email address',
  phone: 'Please enter a valid 10-digit phone number (or 1 + 10 digits)',
  message: 'Please enter a message',
  visitDate: 'Please select a Thursday',
  visitDatePast: 'Please select a Thursday in the future',
} as const;

/**
 * Validates Contact form fields. Returns errors for any invalid field.
 * Use for both enabling the submit button and showing inline errors.
 */
export function validateContactForm(fields: ContactFormFields): FormValidationResult {
  const errors: Record<string, string> = {};
  const { name, email, phone, message } = fields;

  const nameTrimmed = name.trim();
  const emailTrimmed = email.trim();
  const phoneTrimmed = phone.trim();
  const messageTrimmed = message.trim();

  if (nameTrimmed.length > 0 && !isFullName(name)) {
    errors.name = ERRORS.fullName;
  }
  if (emailTrimmed.length > 0 && !isValidEmail(email)) {
    errors.email = ERRORS.email;
  }
  if (phoneTrimmed.length > 0 && !isValidPhone(phone)) {
    errors.phone = ERRORS.phone;
  }
  if (messageTrimmed.length === 0) {
    errors.message = ERRORS.message;
  }

  const valid =
    isFullName(name) &&
    isValidEmail(email) &&
    (phoneTrimmed.length === 0 || isValidPhone(phone)) &&
    messageTrimmed.length > 0;

  return { valid, errors };
}

/**
 * Returns true if the Contact form can be submitted (all required fields valid).
 */
export function canSubmitContact(fields: ContactFormFields): boolean {
  return validateContactForm(fields).valid;
}

/**
 * Validates Schedule Visit form fields. Returns errors for any invalid field.
 */
export function validateScheduleVisitForm(
  fields: ScheduleVisitFormFields
): FormValidationResult {
  const errors: Record<string, string> = {};
  const { name, email, phone, visitDate } = fields;
  const requireVisitDate = fields.requireVisitDate !== false;

  const nameTrimmed = name.trim();
  const emailTrimmed = email.trim();
  const phoneTrimmed = phone.trim();
  const visitDateTrimmed = visitDate.trim();

  if (nameTrimmed.length > 0 && !isFullName(name)) {
    errors.name = ERRORS.fullName;
  }
  if (emailTrimmed.length > 0 && !isValidEmail(email)) {
    errors.email = ERRORS.email;
  }
  if (phoneTrimmed.length > 0 && !isValidPhone(phone)) {
    errors.phone = ERRORS.phone;
  }
  const today = toDateString(new Date());
  if (requireVisitDate) {
    if (visitDateTrimmed.length === 0) {
      errors.visitDate = ERRORS.visitDate;
    } else if (visitDateTrimmed <= today) {
      errors.visitDate = ERRORS.visitDatePast;
    }
  } else if (visitDateTrimmed.length > 0 && visitDateTrimmed <= today) {
    errors.visitDate = ERRORS.visitDatePast;
  }

  const dateOk = requireVisitDate
    ? visitDateTrimmed.length > 0 && visitDateTrimmed > today
    : visitDateTrimmed.length === 0 || visitDateTrimmed > today;

  const valid =
    isFullName(name) &&
    isValidEmail(email) &&
    isValidPhone(phone) &&
    dateOk;

  return { valid, errors };
}

/**
 * Returns true if the Schedule Visit form can be submitted (all required fields valid).
 */
export function canSubmitScheduleVisit(fields: ScheduleVisitFormFields): boolean {
  return validateScheduleVisitForm(fields).valid;
}

/** Family registration step 1 fields (family + emergency contact) */
export interface FamilyRegistrationStep1Fields {
  familyName: string;
  fatherName: string;
  motherName: string;
  phone: string;
  email: string;
  homeAddress: string;
  /** Structured parts from an address autocomplete selection (required). */
  addressCity: string;
  addressState: string;
  addressZip: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
}

/** Family registration step 2 fields */
export interface FamilyRegistrationStep2Fields {
  churchValue: string;
  churchOther: string;
}

/** Family registration step 3 – first child only (required) */
export interface FamilyRegistrationStep3Fields {
  childFirstName: string;
  childLastName: string;
  childBirthdate: string;
  childGrade: string;
}

const FAMILY_ERR = {
  familyName: 'Please enter your family (last) name',
  fullName: 'Please enter first and last name',
  email: 'Please enter a valid email address',
  phone: 'Please enter a valid 10-digit phone number (or 1 + 10 digits)',
  homeAddress: 'Please enter your home address',
  homeAddressSelect: 'Select an address from the list so city, state, and ZIP fill in',
  emergencyName: 'Please enter emergency contact first and last name',
  emergencyPhone: 'Please enter a valid emergency contact phone number',
  church: 'Please select your home church',
  churchOther: 'Please enter your home church name',
  childFirst: 'Please enter the child’s first name',
  childLast: 'Please enter the child’s last name',
  birthdate: 'Please enter the child’s birth date',
  childGrade: 'Please select the child’s grade level',
  signature: 'Signature is required',
  policies: 'You must agree to the BHG Policies and Procedures',
  disciplinary: 'You must agree to the BHG Disciplinary Action Plan',
} as const;

function hasSelectedHomeAddress(fields: Pick<
  FamilyRegistrationStep1Fields,
  'homeAddress' | 'addressCity' | 'addressState' | 'addressZip'
>): boolean {
  return (
    fields.homeAddress.trim() !== '' &&
    fields.addressCity.trim() !== '' &&
    fields.addressState.trim() !== '' &&
    fields.addressZip.trim() !== ''
  );
}

export function validateFamilyRegistrationStep1(fields: FamilyRegistrationStep1Fields): FormValidationResult {
  const errors: Record<string, string> = {};
  const {
    familyName,
    fatherName,
    motherName,
    phone,
    email,
    homeAddress,
    addressCity,
    addressState,
    addressZip,
    emergencyContactName,
    emergencyContactPhone,
  } = fields;

  if (!familyName.trim()) errors.familyName = FAMILY_ERR.familyName;
  if (fatherName.trim().length > 0 && !isFullName(fatherName)) errors.fatherName = FAMILY_ERR.fullName;
  if (!fatherName.trim()) errors.fatherName = FAMILY_ERR.fullName;
  if (motherName.trim().length > 0 && !isFullName(motherName)) errors.motherName = FAMILY_ERR.fullName;
  if (!motherName.trim()) errors.motherName = FAMILY_ERR.fullName;
  if (phone.trim().length > 0 && !isValidPhone(phone)) errors.phone = FAMILY_ERR.phone;
  if (!phone.trim()) errors.phone = FAMILY_ERR.phone;
  if (email.trim().length > 0 && !isValidEmail(email)) errors.email = FAMILY_ERR.email;
  if (!email.trim()) errors.email = FAMILY_ERR.email;
  if (!homeAddress.trim()) {
    errors.homeAddress = FAMILY_ERR.homeAddress;
  } else if (!hasSelectedHomeAddress({ homeAddress, addressCity, addressState, addressZip })) {
    errors.homeAddress = FAMILY_ERR.homeAddressSelect;
  }
  if (emergencyContactName.trim().length > 0 && !isFullName(emergencyContactName)) errors.emergencyContactName = FAMILY_ERR.emergencyName;
  if (!emergencyContactName.trim()) errors.emergencyContactName = FAMILY_ERR.emergencyName;
  if (emergencyContactPhone.trim().length > 0 && !isValidPhone(emergencyContactPhone)) errors.emergencyContactPhone = FAMILY_ERR.emergencyPhone;
  if (!emergencyContactPhone.trim()) errors.emergencyContactPhone = FAMILY_ERR.emergencyPhone;

  const valid =
    familyName.trim() !== '' &&
    isFullName(fatherName) &&
    isFullName(motherName) &&
    isValidPhone(phone) &&
    isValidEmail(email) &&
    hasSelectedHomeAddress({ homeAddress, addressCity, addressState, addressZip }) &&
    isFullName(emergencyContactName) &&
    isValidPhone(emergencyContactPhone);

  return { valid, errors };
}

export function validateFamilyRegistrationStep2(fields: FamilyRegistrationStep2Fields): FormValidationResult {
  const errors: Record<string, string> = {};
  const { churchValue, churchOther } = fields;

  if (!churchValue) errors.churchValue = FAMILY_ERR.church;
  if (churchValue === 'other' && !churchOther.trim()) errors.churchOther = FAMILY_ERR.churchOther;

  const valid = !!churchValue && (churchValue !== 'other' || churchOther.trim() !== '');

  return { valid, errors };
}

export function validateFamilyRegistrationStep3(fields: FamilyRegistrationStep3Fields): FormValidationResult {
  const errors: Record<string, string> = {};
  const { childFirstName, childLastName, childBirthdate, childGrade } = fields;

  if (!childFirstName.trim()) errors.childFirstName = FAMILY_ERR.childFirst;
  if (!childLastName.trim()) errors.childLastName = FAMILY_ERR.childLast;
  if (!childBirthdate.trim()) errors.childBirthdate = FAMILY_ERR.birthdate;
  if (!childGrade.trim()) errors.childGrade = FAMILY_ERR.childGrade;

  const valid =
    childFirstName.trim() !== '' &&
    childLastName.trim() !== '' &&
    childBirthdate.trim() !== '' &&
    childGrade.trim() !== '';

  return { valid, errors };
}
